import { Component, Input, OnInit } from '@angular/core';
import {
  ControlContainer,
  FormArray,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { NotificationsService } from '@app/components/notification/NotificationsService';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { UserDao } from '@app/dao/user/user.dao';
import { Subject, combineLatest, map, shareReplay, takeUntil } from 'rxjs';
import { FormControlOption } from '../../definition/interface/formControlOption.interface';
import { representativeFunctionOptions } from '../../services/ref';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';

@Component({
  selector: 'company-beneficialowner-array',
  templateUrl: './company-beneficialowner-array.component.html',
  styleUrls: ['./company-beneficialowner-array.component.scss'],
  standalone: false,
})
export class CompanyBeneficialownerArrayComponent implements OnInit {
  companyBeneficialOwnerForm: FormArray;
  options: FormControlOption[] = [
    { value: 'M', label: 'Mr', suffix: { title: 'A' } },
    { value: 'F', label: 'Mme', suffix: { title: 'B' } },
  ];
  representativeFunctionOptions = representativeFunctionOptions;
  users: any[];
  col = 3;
  @Input() derrors: any = undefined;
  expandedCards: boolean[] = [];
  sessionConsultant: boolean;
  destroy$ = new Subject<void>();
  countries: any[];
  departments: any[];
  reference$;
  constructor(
    private formBuilder: FormBuilder,
    private controlContainer: ControlContainer,
    private referenceDao: ReferenceDao,
    private userDao: UserDao,
    private subscriptionFacade: SubscriptionFacadeService,
    private notificationService: NotificationsService,
  ) {}

  ngOnInit(): void {
    this.initUsers();
    this.initializeFormArray();
    this.initializeExpandedCards();
    this.initCountyres();
    this.sessionConsultant =
      !!this.subscriptionFacade?.getPledge() &&
      !!this.subscriptionFacade?.getUser()?.sessionConsultant;
  }
  initCountyres(): void {
    const combineLatestObservable = combineLatest([
      this.referenceDao.getCountries(),
      this.referenceDao.getDepartment(),
    ]);

    this.reference$ = combineLatestObservable.pipe(
      takeUntil(this.destroy$),
      map(([countries, departments]) => {
        return { countries, departments };
      }),
      shareReplay(1),
    );
  }
  initUsers(): void {
    this.userDao.getOwners().subscribe(owners => {
      this.users = owners.filter(user =>
        ['MAIN', 'OTHER_USER'].includes(user.relation),
      );
    });
  }

  initializeFormArray(): void {
    this.companyBeneficialOwnerForm = this.controlContainer
      .control as FormArray;
  }

  initializeExpandedCards(): void {
    for (let i = 0; i < this.companyBeneficialOwnerForm.length; i++) {
      this.expandedCards.push(i === 0);
    }
  }

  getAvailableUsers(currentId: number) {
    return this.users.filter(
      user =>
        user.id === currentId ||
        !this.companyBeneficialOwnerForm.controls.some(
          f => f.value.userId === user.id,
        ),
    );
  }

  addFields(): void {
    this.addNewFormGroup();
    this.expandLastItem();
    this.collapseOtherItems();
  }

  addNewFormGroup(): void {
    this.companyBeneficialOwnerForm.push(
      this.formBuilder.group({
        companyId: [''],
        id: [''],
        representativeFunction: [null, Validators.required],
        userFirstName: [''],
        userId: [''],
        userLastName: [''],
        userSharePercentage: [''],
        userBirthDate: [''],
        userBirthPlace: [''],
        userNationality: [''],
        userTaxResidence: [''],
        userPhone: [''],
        userEmail: [''],
        userTitle: [null],
        userBirthDepartment: [''],
        address: this.formBuilder.group({
          city: [''],
          country: [''],
          street: [''],
          street2: [''],
          zipCode: [''],
        }),
        usPerson: [''],
        antiMoneyLaundering: this.formBuilder.group({
          id: [''],
          accountId: [''],
          ownerId: [''],
          beneficialOwnerId: [''],
          profile: ['PHYSICAL'],
          ppe: [false],
          adresseDomiciliation: [false],
          fortuneInfluent: [false],
          secteurRisqueCorruption: [false],
          discretionInhabituelle: [false],
          operationPaysGafi: [false],
          fondPaysGafi: [false],
          operationPaysNonCooperatif: [false],
          fondPaysNonCooperatif: [false],
          personneListeGelsDesAvoirs: [false],
          fondListeGelsDesAvoirs: [false],
          versementPeePerco: [false],
          produitsAtypiques: [false],
          controleActifsFonds: [false],
          compteTitreAugmentationCapital: [false],
          primeAnnuelleUnique: [false],
          contratPrevoyanceSante: [false],
          assuranceEmprunteur: [false],
          contratAssuranceSansClauseRachat: [false],
          contratAnonyme: [false],
          produitsImmobiliersLuxe: [false],
        }),
      }),
    );
    this.companyBeneficialOwnerForm.markAsDirty();
  }

  expandLastItem(): void {
    this.expandedCards.push(true);
  }

  collapseOtherItems(): void {
    for (let i = 0; i < this.expandedCards.length - 1; i++) {
      this.expandedCards[i] = false;
    }
  }

  deleteFields(index: number): void {
    this.companyBeneficialOwnerForm.removeAt(index);
  }

  ngOnChanges(): void {
    if (this.companyBeneficialOwnerForm) {
      this.updateFormControlsValidity();
    }
  }

  updateFormControlsValidity(): void {
    this.companyBeneficialOwnerForm.controls.forEach(control => {
      control.updateValueAndValidity();
      if (!control.get('representativeFunction')?.value) {
        this.notificationService.error(
          'Veuillez séléctionner une fonction pour le bénéficiaire effectif ' +
            ((control.get('userId')?.value &&
              this.users.find(u => u.id === control.get('userId')?.value)
                ?.label) ??
              `${control.get('userFirstName')?.value} ${control.get('userLastName')?.value}`),
        );
      }
    });
  }

  toggleCardExpanded(index: number): void {
    if (this.companyBeneficialOwnerForm.length === 1) {
      this.expandedCards[index] = true;
    } else {
      this.expandedCards[index] = !this.expandedCards[index];
    }
  }

  isCardExpanded(index: number): boolean {
    return this.expandedCards[index];
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }
}
